/**
 * useFilePicker — hidden <input type=file> wrapper so a styled button can open
 * the OS file dialog, plus a mapper from picked/dropped files to the UploadFile
 * shape. Real client-side; when B1b lands these File objects POST to
 * /api/v1/projects/{id}/documents (see Frontend_action_contract.md).
 */
import { useCallback, useRef, type ReactElement } from "react";

import type { UploadFile, UploadFileType } from "./mock";

const EXT_TYPE: Record<string, UploadFileType> = {
  pdf: "pdf",
  xlsx: "xlsx",
  xls: "xlsx",
  dwg: "dwg",
  ifc: "dwg",
};

function fmtSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${bytes} B`;
}

export function fileToUploadFile(f: File): UploadFile {
  const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
  return { name: f.name, type: EXT_TYPE[ext] ?? "other", size: fmtSize(f.size) };
}

export function filesToUploadFiles(list: FileList | File[]): UploadFile[] {
  return Array.from(list).map(fileToUploadFile);
}

interface UseFilePickerOptions {
  multiple?: boolean;
  directory?: boolean;
  /** Receives the real File objects so callers can upload them. */
  onFiles: (files: File[]) => void;
}

interface UseFilePicker {
  open: () => void;
  /** Render this once in the component tree; it's a hidden input. */
  input: ReactElement;
}

export function useFilePicker({
  multiple = true,
  directory = false,
  onFiles,
}: UseFilePickerOptions): UseFilePicker {
  const ref = useRef<HTMLInputElement>(null);

  const open = useCallback(() => ref.current?.click(), []);

  const input = (
    <input
      ref={ref}
      type="file"
      multiple={multiple}
      // webkitdirectory is non-standard; cast through a record to set it.
      {...(directory ? ({ webkitdirectory: "" } as Record<string, string>) : {})}
      style={{ display: "none" }}
      onChange={(e) => {
        if (e.target.files && e.target.files.length > 0) onFiles(Array.from(e.target.files));
        e.target.value = "";
      }}
    />
  );

  return { open, input };
}
